import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Snowflake, Award, Package, Users, Truck, Repeat, ChevronDown, Check, X, MinusCircle, Quote, Clock, Gift, Sparkles, Zap, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import BoxCard from "@/components/BoxCard";
import PostcodeChecker from "@/components/PostcodeChecker";
import CutoffCountdown from "@/components/CutoffCountdown";
import { FAQ_GROUPS } from "@/data/faq";
import { TESTIMONIALS, STANDARDS, FOUNDER_STORY, COMPARISON } from "@/data/social";
import { CertificationSeal } from "@/components/StandardSeals";
import SEOHead, { ORG_SCHEMA, LOCAL_BUSINESS_SCHEMA } from "@/components/SEOHead";
import Reveal from "@/components/Reveal";
import DeliveryCadence from "@/components/DeliveryCadence";
import LazyVideo from "@/components/LazyVideo";
import HeroMedia from "@/components/HeroMedia";
import Img from "@/components/Img";

const TRUST_ITEMS = [
  { icon: Truck, label: "Free Delivery · Every Order" },
  { icon: MapPin, label: "WA Owned · Perth Local" },
  { icon: Award, label: "Premium Australian Meat" },
  { icon: Award, label: "Halal Certified" },
  { icon: Package, label: "Vacuum Packed Fresh" },
  { icon: Snowflake, label: "Delivered Cold · Perth Metro" },
  { icon: Award, label: "Restaurant Quality" },
  { icon: Users, label: "Family Value" },
  { icon: Repeat, label: "Subscribe & Save 5%" },
  { icon: Clock, label: "Order Thu 11pm · Pack Fri · Deliver Sat" },
];

const CATEGORIES = [
  { slug: "beef", name: "Beef", image: "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=800&q=80" },
  { slug: "chicken", name: "Chicken", image: "https://images.unsplash.com/photo-1587593810167-a84920ea0781?w=800&q=80" },
  { slug: "lamb", name: "Lamb", image: "https://images.unsplash.com/photo-1602470520998-f4a52199a3d6?w=800&q=80" },
  { slug: "burgers", name: "Burgers", image: "https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=800&q=80" },
  { slug: "gluten-free-sausages", name: "Sausages", image: "https://images.unsplash.com/photo-1624772398061-bbfa87ec6b5a?w=800&q=80" },
  { slug: "gourmet-jerky", name: "Jerky", image: "https://images.unsplash.com/photo-1652209695374-7a91c243f12f?w=800&q=80" },
];

const TOP_FAQ_SLUGS = ["plans-boxes", "subscription-billing", "sourcing-standards", "delivery"];

function HomeFAQAccordion() {
  const items = [];
  TOP_FAQ_SLUGS.forEach((slug) => {
    const g = FAQ_GROUPS.find((x) => x.slug === slug);
    if (g && g.items.length) items.push(g.items[0]);
  });
  const inside = FAQ_GROUPS.find((x) => x.slug === "whats-inside");
  const wagyu = inside?.items.find((it) => it.q.toLowerCase().includes("wagyu beef burger"));
  if (wagyu) items.push(wagyu);
  const fresh = FAQ_GROUPS.find((x) => x.slug === "freshness-storage");
  const guarantee = fresh?.items.find((it) => it.q.toLowerCase().includes("unhappy"));
  if (guarantee) items.push(guarantee);

  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div>
      {items.map((it, i) => (
        <div key={i} className="border-b border-[color:var(--tbb-line)]" data-testid={`home-faq-${i}`}>
          <button
            onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
            className="w-full flex items-start justify-between gap-4 py-5 text-left"
            aria-expanded={openIdx === i}
          >
            <span className="font-display text-lg leading-snug pr-4">{it.q}</span>
            <ChevronDown
              size={18}
              className={`shrink-0 mt-1 text-[color:var(--tbb-burgundy)] transition-transform ${openIdx === i ? "rotate-180" : ""}`}
            />
          </button>
          {openIdx === i && (
            <div className="pb-6 pr-8 text-sm text-[color:var(--tbb-muted)] leading-relaxed">
              {it.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Home() {
  const [boxes, setBoxes] = useState([]);

  useEffect(() => {
    api.get("/boxes").then((r) => setBoxes(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Route-scoped resource hints — the founder-story craft video sits
          well below the fold, so we DO NOT eagerly preload it. LazyVideo
          uses IntersectionObserver to start the fetch only when it enters
          the viewport (~200px early). Preloading here would hurt LCP on
          the hero without any user-visible benefit. */}
      <SEOHead
        title="THE BUTCHER BOX — Premium Perth Meat Subscription & Delivery"
        description="Perth's premium meat subscription — every cut halal certified. 7 Signature Boxes or build your own from 56 restaurant-quality Australian cuts. Subscribe & save 5% + free Wagyu Beef Burgers every delivery. Order Thu 11pm · delivered Sat across Perth metro."
        path="/"
        jsonLd={[ORG_SCHEMA, LOCAL_BUSINESS_SCHEMA]}
      />
      {/* TRUST STRIP — top of page.
          Sept 2026: added `cursor-default` + `pointer-events-none` on the
          child items so users can't "dead-click" the pills. Session
          recordings had shoppers tapping the pills expecting to jump
          to a details page (13% of interior clicks on Home were on
          these non-interactive elements). The whole strip is
          decorative — links now live below in the hero CTAs. */}
      <section className="border-b border-[color:var(--tbb-line)] bg-white overflow-hidden cursor-default" data-testid="trust-strip">
        <div className="marquee-track py-4 pointer-events-none">
          {[...TRUST_ITEMS, ...TRUST_ITEMS].map((t, i) => {
            const Icon = t.icon;
            return (
              <div key={i} className="flex items-center gap-3 shrink-0 px-6 select-none">
                <Icon size={16} className="text-[color:var(--tbb-burgundy)]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em]">{t.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* HERO — LCP-critical.
          Aug 2026: replaced the autoplay video hero with a static family
          portrait rendered as a responsive <picture> (WebP 480/768/1200
          + JPEG fallback). Mobile gets ~25 KB, retina desktop ~94 KB,
          Facebook/Instagram in-app browsers get the same clean static
          (no more <video> autoplay bounce on iOS FBAN). See HeroMedia.jsx. */}
      <section className="hero-wrap">
        <HeroMedia alt="A family unboxing THE BUTCHER BOX in their kitchen" />
        <div className="hero-veil" />
        <div className="grain-overlay" />
        <div className="relative tbb-container z-10 flex flex-col justify-center min-h-[92vh] pt-24 pb-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-6" data-testid="hero-eyebrow">
              <span className="px-3 py-1.5 bg-[color:var(--tbb-burgundy)] text-white text-[10px] font-bold uppercase tracking-[0.2em]">
                Established Perth WA
              </span>
              <span className="text-white/85 text-[10px] font-bold uppercase tracking-[0.2em]" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.75)" }}>
                · Free Delivery Every Order
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight uppercase" data-testid="hero-headline">
              Premium Australian Meat<br />
              <span className="text-[color:var(--tbb-burgundy)]">Delivered</span> To Your Door
            </h1>

            {/* Above-fold social proof strip */}
            <div className="mt-5 flex items-center gap-3 flex-wrap" data-testid="hero-social-proof">
              <div className="flex -space-x-2" aria-hidden="true">
                {[
                  { bg: "#8B2434", initials: "SR" },
                  { bg: "#3d2817", initials: "MK" },
                  { bg: "#2a2a2a", initials: "JT" },
                ].map((a) => (
                  <div
                    key={a.initials}
                    className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold text-white tracking-wider shadow-sm"
                    style={{ backgroundColor: a.bg }}
                  >
                    {a.initials}
                  </div>
                ))}
                <div className="w-8 h-8 rounded-full border-2 border-white bg-white/95 text-[color:var(--tbb-ink)] flex items-center justify-center text-[9px] font-bold shadow-sm">
                  +
                </div>
              </div>
              <div className="flex items-center gap-1.5" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.7)" }}>
                <div className="flex gap-0.5" aria-label="4.9 out of 5 stars">
                  {[0,1,2,3,4].map((i) => (
                    <svg key={i} width="13" height="13" viewBox="0 0 20 20" fill="#f5c451" aria-hidden="true">
                      <path d="M10 1.5l2.6 5.3 5.8.85-4.2 4.1 1 5.8L10 14.8l-5.2 2.75 1-5.8-4.2-4.1 5.8-.85L10 1.5z"/>
                    </svg>
                  ))}
                </div>
                <span className="text-white font-bold text-sm">4.9</span>
                <span className="text-white/80 text-xs">·</span>
                <span className="text-white/90 text-xs font-medium">Loved by 500+ Perth families</span>
              </div>
            </div>

            <p className="mt-6 text-lg text-white/85 max-w-xl leading-relaxed">
              Restaurant-quality beef, lamb, chicken, burgers, smallgoods and gourmet meats — packed fresh and delivered cold.
            </p>

            {/* Halal universal claim — top-of-fold visibility */}
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/20 backdrop-blur" data-testid="hero-halal-line">
              <Award size={12} className="text-[color:var(--tbb-burgundy)]" />
              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-white">100% Halal Certified · Every Cut, Every Box</span>
            </div>

            {/* Subscription value prop — printed on the hero, replaces the third CTA */}
            <div className="mt-8 flex items-center gap-3 max-w-xl" data-testid="hero-sub-callout">
              <span className="inline-flex items-center justify-center w-11 h-11 shrink-0 bg-[color:var(--tbb-burgundy)] text-white font-display text-xl">
                5%
              </span>
              <p className="text-sm md:text-base text-white leading-snug" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.65)" }}>
                <strong className="text-white">Subscribe &amp; save 5% off every order</strong>
                <span className="text-white/85"> + a free pack of Wagyu Beef Burgers with every delivery.</span>
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3 items-stretch" data-testid="hero-ctas">
              <Link
                to="/boxes"
                className="btn-burgundy w-[240px] justify-center"
                data-testid="hero-shop-boxes"
              >
                Shop Boxes <ArrowRight size={14} />
              </Link>
              <Link
                to="/build-your-own"
                className="btn-ghost-light w-[240px] justify-center"
                data-testid="hero-build-your-own"
              >
                Build Your Own Box <ArrowRight size={14} />
              </Link>
            </div>

            {/* Honest urgency — real weekly cutoff countdown, not fake FOMO */}
            <div className="mt-5" data-testid="hero-countdown-slot">
              <CutoffCountdown variant="light" />
            </div>

            {/* Postcode delivery checker */}
            <div className="mt-10" data-testid="hero-postcode-slot">
              <PostcodeChecker variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — steps above, two-choice picker below */}
      <section className="py-16 md:py-20 border-b border-[color:var(--tbb-line)]" data-testid="how-it-works-compact">
        <div className="tbb-container">
          {/* Subscription delivery cadence — prominent, above steps */}
          <div className="mb-14 md:mb-16">
            <div className="text-center mb-6 max-w-xl mx-auto">
              <div className="eyebrow mb-2 justify-center inline-flex">Every Week · One Cycle</div>
              <h2 className="font-display text-3xl md:text-4xl uppercase leading-tight">
                Order Thu 11pm. Packed Fri. Cold to your door Sat.
              </h2>
              <p className="text-sm text-[color:var(--tbb-muted)] mt-2">
                Every subscription runs on the same weekly rhythm — predictable, fresh, on time.
              </p>
            </div>
            <DeliveryCadence data-testid="home-cadence-strip" />
          </div>

          {/* Steps — pictured process cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                n: "01",
                icon: Package,
                image: "/hiw/step-1-browse.jpg",
                title: "Pick your path",
                body: "Choose one of 7 Signature boxes OR build your own from our full range — right from the couch, Perth metro delivery.",
              },
              {
                n: "02",
                icon: Truck,
                image: "/hiw/step-2-unboxing.jpg",
                title: "Packed & delivered cold",
                body: "Every cut vacuum sealed and labelled at our Malaga butchery, packed cold and delivered to your door.",
              },
              {
                n: "03",
                icon: Repeat,
                image: "/hiw/cooking-steak.jpg",
                title: "Cook & repeat",
                body: "Restaurant-quality meals at home. Subscribe for 5% off + free Wagyu burgers every delivery.",
              },
            ].map((s) => {
              const I = s.icon;
              return (
                <div
                  key={s.title}
                  className="tbb-card overflow-hidden"
                  data-testid={`hiw-compact-${s.n}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[color:var(--tbb-bg-soft)]">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="img-cover"
                      loading="lazy"
                      onError={(e) => {
                        const fb = "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=1200&q=80";
                        if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
                      }}
                    />
                    <div className="absolute top-4 left-4 bg-black/60 backdrop-blur text-white text-xs font-bold px-3 py-1 uppercase tracking-widest">
                      Step {s.n}
                    </div>
                  </div>
                  <div className="p-6">
                    <I size={22} className="text-[color:var(--tbb-burgundy)] mb-3" />
                    <h4 className="font-display text-2xl mb-2">{s.title}</h4>
                    <p className="text-sm text-[color:var(--tbb-muted)] leading-relaxed">{s.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-8 mb-16 md:mb-20">
            <Link
              to="/how-it-works"
              className="text-xs font-bold uppercase tracking-[0.2em] underline underline-offset-4 hover:text-[color:var(--tbb-burgundy)]"
              data-testid="hiw-compact-more"
            >
              See the full 5-step process →
            </Link>
          </div>

          {/* Two-choice picker — bold, engaging, unmissable */}
          <div className="mb-8 md:mb-10 text-center">
            <div className="eyebrow mb-3 justify-center inline-flex">Two ways to shop</div>
            <h2 className="font-display text-3xl md:text-5xl uppercase leading-tight">
              Choose your <span className="text-[color:var(--tbb-burgundy)]">adventure.</span>
            </h2>
          </div>
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-6xl mx-auto" data-testid="two-ways-to-shop">
            {/* OR divider — desktop only */}
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center w-14 h-14 bg-white border-2 border-[color:var(--tbb-ink)] rounded-full font-display text-xl uppercase pointer-events-none">
              or
            </div>

            {/* Option 1 — Signature box (BURGUNDY) */}
            <Reveal index={0} cols={2} step={150}>
            <Link
              to="/boxes"
              className="group relative overflow-hidden bg-[color:var(--tbb-burgundy)] text-white p-8 md:p-12 flex flex-col justify-between min-h-[380px] md:min-h-[480px] hover:bg-[color:var(--tbb-ink)] transition-all duration-300"
              data-testid="hiw-option-1"
            >
              {/* Decorative gradient wash */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />

              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 mb-6" data-testid="option-1-badge">
                  <Sparkles size={12} />
                  <span className="text-[10px] font-black uppercase tracking-[0.24em]">Most Popular</span>
                </div>
                <div className="mb-4">
                  <Package size={40} strokeWidth={1.5} className="opacity-80" />
                </div>
                <div className="font-display text-4xl md:text-6xl uppercase leading-[0.9] mb-4">
                  Shop<br />Signature<br />Boxes
                </div>
                <p className="text-sm md:text-base text-white/85 max-w-sm leading-relaxed">
                  <strong className="text-white">7 hand-picked boxes</strong> ready to go — Starter to Grill Master&apos;s. Best-value curation from our butchers.
                </p>
              </div>

              <div className="relative mt-8 flex items-end justify-between border-t border-white/25 pt-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/70 mb-1">Starts from</div>
                  <div className="font-display text-4xl">$99</div>
                </div>
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em]">
                  Shop Now
                  <ArrowRight size={22} className="transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
            </Reveal>

            {/* Option 2 — Build your own (INK / CHARCOAL) */}
            <Reveal index={1} cols={2} step={150}>
            <Link
              to="/build-your-own"
              className="group relative overflow-hidden bg-[color:var(--tbb-ink)] text-white p-8 md:p-12 flex flex-col justify-between min-h-[380px] md:min-h-[480px] hover:bg-[color:var(--tbb-burgundy)] transition-all duration-300"
              data-testid="hiw-option-2"
            >
              {/* Decorative gradient wash */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/8 to-transparent" />

              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 mb-6" data-testid="option-2-badge">
                  <Zap size={12} />
                  <span className="text-[10px] font-black uppercase tracking-[0.24em]">Your Way</span>
                </div>
                <div className="mb-4">
                  <Users size={40} strokeWidth={1.5} className="opacity-80" />
                </div>
                <div className="font-display text-4xl md:text-6xl uppercase leading-[0.9] mb-4">
                  Build<br />Your<br />Own Box
                </div>
                <p className="text-sm md:text-base text-white/85 max-w-sm leading-relaxed">
                  <strong className="text-white">56 restaurant-quality cuts</strong> — beef, lamb, chicken, Wagyu burgers, biltong &amp; more. Total control.
                </p>
              </div>

              <div className="relative mt-8 flex items-end justify-between border-t border-white/25 pt-6">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/70 mb-1">Min order</div>
                  <div className="font-display text-4xl">$99</div>
                </div>
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em]">
                  Build Now
                  <ArrowRight size={22} className="transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CURATED BOXES */}
      <section className="py-24" data-testid="curated-boxes-section">
        <div className="tbb-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow mb-3">Signature Boxes</div>
              <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight">
                Seven Signature boxes<br />to suit every need.
              </h2>
            </div>
            <p className="text-sm text-[color:var(--tbb-muted)] max-w-md">
              Each box is hand-picked by our butchers. Subscribe to any box for 5% off + a free pack of Wagyu Beef Burgers every delivery.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {/* Family Feast first — MOST POPULAR */}
            {[...boxes].sort((a, b) => (a.slug === "family-feast-box" ? -1 : b.slug === "family-feast-box" ? 1 : 0)).map((b, i) => <BoxCard key={b.slug} box={b} index={i} hideFrequency={true} />)}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-[color:var(--tbb-bg-soft)] py-24">
        <div className="tbb-container">
          <div className="mb-10">
            <div className="eyebrow mb-3">Shop By Category</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight">
              Build your own box.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/build-your-own?category=${c.slug}`}
                className="category-tile aspect-[3/4] bg-black text-white relative overflow-hidden group"
                data-testid={`category-tile-${c.slug}`}
              >
                <img src={c.image} alt={c.name} className="img-cover opacity-80 group-hover:opacity-100 transition" loading="lazy" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute bottom-0 p-4">
                  <div className="font-display text-xl uppercase leading-none">{c.name}</div>
                  <div className="overline text-white/70 mt-1">Shop →</div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/build-your-own" className="btn-primary" data-testid="cta-build-your-own">
              Build Your Own Box <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* SUBSCRIPTION HERO — moved below the Build Your Own Box section */}
      <section className="relative bg-[color:var(--tbb-cream)] text-[color:var(--tbb-ink)] overflow-hidden py-14 md:py-20 border-y border-[color:var(--tbb-line)]" data-testid="subscription-hero-section">
        {/* subtle burgundy gradient wash */}
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{background: "radial-gradient(600px 400px at 15% 20%, rgba(139,36,52,0.18), transparent 60%), radial-gradient(500px 400px at 85% 80%, rgba(139,36,52,0.10), transparent 55%)"}} />
        <div className="relative tbb-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-[color:var(--tbb-burgundy)] text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.28em] mb-6" data-testid="sub-hero-eyebrow">
                <Sparkles size={11} strokeWidth={2.5} /> The Butcher Club · Members Only
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] uppercase mb-6">
                Every delivery.<br />
                <span className="text-[color:var(--tbb-burgundy)]">Free Wagyu Burgers</span><br />
                &amp; 5% off, forever.
              </h2>
              <p className="text-[color:var(--tbb-muted)] text-base md:text-lg mb-10 max-w-xl leading-relaxed">
                Join The Butcher Club and get a <strong className="text-[color:var(--tbb-ink)]">complimentary pack of gourmet Wagyu Beef Burgers ($18 value) with every single delivery — forever</strong>. Plus 5% off every order and priority Perth-metro dispatch. Pause, skip or cancel in one tap.
              </p>

              <ul className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm mb-10" data-testid="sub-hero-benefits">
                {[
                  { i: Gift, t: "Free Wagyu Burgers · $18 every delivery" },
                  { i: MinusCircle, t: "Plus 5% off every order, forever" },
                  { i: Zap, t: "Priority Perth-metro dispatch" },
                  { i: Repeat, t: "Weekly, fortnightly or monthly" },
                  { i: Clock, t: "Pause / skip in one tap" },
                  { i: X, t: "No lock-in · cancel anytime" },
                ].map((b) => {
                  const I = b.i;
                  return (
                    <li key={b.t} className="flex items-start gap-2.5 text-[color:var(--tbb-ink)] leading-snug font-medium">
                      <I size={16} className="mt-0.5 text-[color:var(--tbb-burgundy)] shrink-0" strokeWidth={2.5} />
                      <span>{b.t}</span>
                    </li>
                  );
                })}
              </ul>

              <div className="flex flex-wrap items-center gap-4">
                <Link to="/boxes" className="btn-burgundy text-base py-4 px-6" data-testid="sub-cta-boxes">
                  Join The Butcher Club <ArrowRight size={16} />
                </Link>
                <div className="text-[11px] uppercase tracking-widest text-[color:var(--tbb-muted)] font-bold">
                  <span className="text-[color:var(--tbb-burgundy)]">Starting from $94</span> · Cancel anytime
                </div>
              </div>
            </div>

            {/* Right visual — product-focused, replaces old couple image */}
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] overflow-hidden">
                <Img
                  src="/hiw/butcher-box.jpg"
                  alt="THE BUTCHER BOX product delivery — vacuum-sealed steaks, mince and burgers"
                  width={1000}
                  height={1250}
                  className="img-cover"
                  onError={(e) => {
                    const fb = "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=1200&q=80";
                    if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
                  }}
                />
                {/* Gold complimentary flag */}
                <div className="absolute top-4 left-4 bg-[color:var(--tbb-burgundy)] text-white px-3 py-2 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5" data-testid="sub-hero-complimentary">
                  <Gift size={12} strokeWidth={2.5} /> Complimentary
                </div>
              </div>
              {/* Free burgers callout underneath (was overlaid before — cleaner in a card) */}
              <div className="mt-4 bg-[color:var(--tbb-burgundy)] text-white p-5 flex items-start gap-4">
                <Gift size={28} strokeWidth={2.5} className="mt-1 shrink-0" />
                <div>
                  <div className="overline mb-1 text-white/80">Every subscription delivery</div>
                  <div className="font-display text-xl md:text-2xl leading-tight">
                    Free Pack of Wagyu Beef Burgers
                  </div>
                  <div className="text-[11px] text-white/80 mt-1">~$18 value · yours to keep, every single box</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IN THE KITCHEN — full-bleed branded feature */}
      <section className="bg-[color:var(--tbb-bg-soft)]">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative aspect-square lg:aspect-auto lg:min-h-[600px] order-2 lg:order-1">
            <img
              src="/img/home-story.png"
              alt="Butcher Box contents laid out"
              className="img-cover"
              loading="lazy"
              decoding="async"
              width={1200}
              height={1200}
              data-testid="kitchen-feature-image"
            />
          </div>
          <div className="flex flex-col justify-center p-10 md:p-16 order-1 lg:order-2" data-testid="kitchen-feature">
            <div className="eyebrow mb-4">In Australian Kitchens</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.95] mb-6">
              Every box, hand-packed.<br />
              <span className="text-[color:var(--tbb-burgundy)]">Every cut, labelled.</span>
            </h2>
            <p className="text-[color:var(--tbb-muted)] leading-relaxed mb-8 max-w-md">
              We vacuum-pack every steak, roast and portion by hand. Individually labelled by cut so you know exactly what&apos;s for dinner tonight — and next Tuesday.
            </p>
            <ul className="space-y-2 text-sm mb-8">
              {["Individually labelled cuts", "Vacuum sealed for freshness", "Chilled from butcher to door", "No mystery meat, ever"].map((l) => (
                <li key={l} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-[color:var(--tbb-burgundy)]" /> {l}
                </li>
              ))}
            </ul>
            <div>
              <Link to="/boxes" className="btn-burgundy" data-testid="kitchen-feature-cta">
                Explore the boxes <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SOURCING & STANDARDS */}
      <section className="py-24 bg-[color:var(--tbb-bg-soft)]" data-testid="standards-section">
        <div className="tbb-container">
          <div className="mb-12 max-w-2xl">
            <div className="eyebrow mb-3">Superior Standards</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight">
              No hormones. No shortcuts. <span className="text-[color:var(--tbb-burgundy)]">No compromise.</span>
            </h2>
            <p className="text-sm text-[color:var(--tbb-muted)] mt-4">
              Every cut we ship meets seven non-negotiable standards. Fully traceable, fully Australian.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {STANDARDS.map((s) => (
              <div key={s.title} className="tbb-card p-6 flex gap-5 items-center" data-testid={`standard-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <CertificationSeal label={s.seal || s.title} />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl mb-2">{s.title}</div>
                  <div className="text-sm text-[color:var(--tbb-muted)] leading-relaxed">{s.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24" data-testid="testimonials-section">
        <div className="tbb-container">
          <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="eyebrow mb-3">Reviews</div>
              <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight">
                Why Perth chooses<br />
                <span className="text-[color:var(--tbb-burgundy)]">The Butcher Box.</span>
              </h2>
            </div>
            <div className="text-sm text-[color:var(--tbb-muted)] max-w-xs">
              Loved by Perth families across the metro.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="tbb-card p-6 flex flex-col" data-testid={`testimonial-${i}`}>
                <Quote size={20} className="text-[color:var(--tbb-burgundy)] mb-4" />
                <div className="flex gap-0.5 mb-3 text-[color:var(--tbb-burgundy)] text-sm">★★★★★</div>
                <p className="text-sm leading-relaxed text-[color:var(--tbb-ink)] mb-6 flex-1">&ldquo;{t.quote}&rdquo;</p>
                <div className="border-t border-[color:var(--tbb-line)] pt-4">
                  <div className="font-display text-base">{t.name}</div>
                  <div className="text-xs text-[color:var(--tbb-muted)]">{t.location}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--tbb-burgundy)] font-bold mt-1">{t.plan}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER / BRAND STORY — now with butcher's-hands craft video */}
      <section className="bg-[color:var(--tbb-burgundy-dark)] text-white" data-testid="founder-story">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative aspect-square lg:aspect-auto lg:min-h-[560px] bg-[color:var(--tbb-burgundy-dark)]">
            <LazyVideo
              src="/media/craft-720.mp4"
              poster="/media/craft-poster.jpg"
              className="absolute inset-0"
              ariaLabel="A Perth family unboxing their weekly Butcher Box delivery"
              testId="founder-craft-video"
            />
            {/* Cinematic bottom-fade for text/badge legibility */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 bg-black/60 backdrop-blur px-3 py-1.5">
              <span className="relative inline-flex h-1.5 w-1.5">
                <span className="absolute inset-0 rounded-full bg-[color:var(--tbb-burgundy)] animate-ping opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[color:var(--tbb-burgundy)]" />
              </span>
              <span className="text-[10px] font-black uppercase tracking-[0.24em]">Delivered This Week</span>
            </div>
            <div className="absolute bottom-4 left-4 right-4 z-10 flex items-end justify-between text-white">
              <div className="font-display text-xl uppercase leading-tight">Real Perth Kitchens</div>
              <div className="text-[10px] uppercase tracking-widest text-white/70">Free Delivery · Every Order</div>
            </div>
          </div>
          <div className="flex flex-col justify-center p-10 md:p-16">
            <div className="overline text-[color:var(--tbb-burgundy)] mb-4">Our Story</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.95] mb-6">
              {FOUNDER_STORY.headline}
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">{FOUNDER_STORY.para1}</p>
            <p className="text-white/70 leading-relaxed mb-8">{FOUNDER_STORY.para2}</p>
            <div>
              <Link to="/boxes" className="btn-burgundy" data-testid="founder-cta">
                Start your first box <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-24" data-testid="comparison-section">
        <div className="tbb-container">
          <div className="mb-12 text-center max-w-2xl mx-auto">
            <div className="eyebrow mb-3 justify-center inline-flex">The Comparison</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight">
              How we compare to<br />
              the alternatives.
            </h2>
          </div>
          <div className="overflow-x-auto tbb-card">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="bg-[color:var(--tbb-ink)] text-white">
                  <th className="text-left px-6 py-5 text-[11px] uppercase tracking-widest font-bold w-2/5">Feature</th>
                  <th className="px-6 py-5">
                    <div className="font-display text-lg leading-tight">THE BUTCHER<br /><span className="text-[color:var(--tbb-burgundy)]">BOX</span></div>
                  </th>
                  <th className="px-6 py-5 text-white/70">
                    <div className="text-[11px] uppercase tracking-widest font-bold">Boutique Butcher</div>
                  </th>
                  <th className="px-6 py-5 text-white/70">
                    <div className="text-[11px] uppercase tracking-widest font-bold">Supermarket</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.rows.map((r, i) => {
                  // Render ✓ / ✗ as text characters (not lucide icons) so
                  // pre-rendered HTML from prerender.jsx contains the marks —
                  // ads-team requirement for SSR indexability.
                  const cell = (val) => {
                    if (val === true) return <span className="inline-block text-lg font-bold" aria-label="Yes">✓</span>;
                    if (val === "partial") return <span className="inline-block text-sm text-[color:var(--tbb-muted)]" aria-label="Partial">◐</span>;
                    return <span className="inline-block text-lg text-[color:var(--tbb-muted)]" aria-label="No">✗</span>;
                  };
                  return (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[color:var(--tbb-bg-soft)]"} data-testid={`compare-row-${i}`}>
                      <td className="px-6 py-4 font-bold">{r.label}</td>
                      <td className="px-6 py-4 text-center text-[color:var(--tbb-burgundy)]" data-testid={`compare-tbb-${i}`}>{cell(r.tbb)}</td>
                      <td className="px-6 py-4 text-center" data-testid={`compare-boutique-${i}`}>{cell(r.boutique)}</td>
                      <td className="px-6 py-4 text-center" data-testid={`compare-supermarket-${i}`}>{cell(r.supermarket)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-center mt-8">
            <Link to="/boxes" className="btn-burgundy" data-testid="comparison-cta">
              See the boxes <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="py-24">
        <div className="tbb-container grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-12">
          <div>
            <div className="eyebrow mb-3">FAQ</div>
            <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight mb-6">
              Peace of mind, delivered.
            </h2>
            <p className="text-sm text-[color:var(--tbb-muted)] mb-8 leading-relaxed">
              We&apos;ve answered the most-asked questions about our boxes, subscriptions and sourcing. Can&apos;t find yours? Reach out any time.
            </p>
            <Link to="/faq" className="btn-outline" data-testid="home-faq-see-all">
              See all FAQs <ArrowRight size={14} />
            </Link>
          </div>
          <div data-testid="home-faq-preview">
            <HomeFAQAccordion />
          </div>
        </div>
      </section>

      {/* WHOLESALE / EXPORT PROMO */}
      <section className="bg-[color:var(--tbb-ink)] text-white py-24">
        <div className="tbb-container grid grid-cols-1 md:grid-cols-2 gap-8">
          <Link to="/wholesale" className="relative aspect-[4/3] block overflow-hidden group" data-testid="promo-wholesale">
            <img src="https://images.pexels.com/photos/4253300/pexels-photo-4253300.jpeg" alt="Wholesale" className="img-cover group-hover:scale-105 transition duration-700" loading="lazy" decoding="async" />
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition" />
            <div className="absolute bottom-0 p-8">
              <div className="overline text-white/70 mb-2">Wholesale</div>
              <h3 className="font-display text-3xl md:text-4xl uppercase leading-tight max-w-xs">
                Supply for restaurants, cafes & catering
              </h3>
              <div className="mt-4 text-sm underline">Apply for account →</div>
            </div>
          </Link>
          <Link to="/export" className="relative aspect-[4/3] block overflow-hidden group" data-testid="promo-export">
            <img src="https://images.unsplash.com/photo-1577705998148-6da4f3963bc8?w=1600&q=75" alt="Export" className="img-cover group-hover:scale-105 transition duration-700" loading="lazy" decoding="async" />
            <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition" />
            <div className="absolute bottom-0 p-8">
              <div className="overline text-white/70 mb-2">Export</div>
              <h3 className="font-display text-3xl md:text-4xl uppercase leading-tight max-w-xs">
                Premium Australian meat for global markets
              </h3>
              <div className="mt-4 text-sm underline">Malaysia · Singapore · UAE →</div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
